import React, { useState } from "react";
import { IconButton, Menu, MenuItem, CircularProgress } from "@mui/material";
import MoreIcon from "@diligentcorp/atlas-react-icons/dist/esm/lens/More.js";
import { downloadPolicyDocumentComplete } from "../services/policyDownloadService";
import type { PolicyDownloadRequest } from "../services/policyDownloadService";

interface ActionsMenuProps {
  rowId: number;
  policyName: string;
  versionNumber: string;
  agencyId: string;
  platform_org_id: string;
  language: string;
  onSendEmail: (policyId: number, policyName: string) => void;
}

const ActionsMenu: React.FC<ActionsMenuProps> = ({
  rowId,
  policyName,
  versionNumber,
  agencyId,
  platform_org_id,
  language,
  onSendEmail,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      const downloadRequest: PolicyDownloadRequest = {
        agencyId: agencyId,
        platform_org_id: platform_org_id,
        policyDocument: {
          id: rowId.toString(),
          name: policyName,
        },
        latestVersion: {
          versionNumber: versionNumber,
        },
        languageCode: language || "EN", // Use language from policy data, fallback to EN
      };

      await downloadPolicyDocumentComplete(downloadRequest);
      handleClose();
    } catch (error) {
      console.error("Download failed:", error);
      // You might want to show a toast notification here
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSendEmail = () => {
    onSendEmail(rowId, policyName);
    handleClose();
  };

  return (
    <>
      <IconButton
        aria-label="more"
        id={`menu-button-${rowId}`}
        aria-controls={open ? `menu-${rowId}` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        size="small"
      >
        <MoreIcon />
      </IconButton>
      <Menu
        id={`menu-${rowId}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": `menu-button-${rowId}`,
        }}
      >
        <MenuItem onClick={handleDownload} disabled={isDownloading}>
          {isDownloading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Downloading...
            </>
          ) : (
            "Download"
          )}
        </MenuItem>
        {/* TODO: Enable Send Email functionality when ready */}
        {false && <MenuItem onClick={handleSendEmail}>Send Email</MenuItem>}
      </Menu>
    </>
  );
};

export default ActionsMenu;
